<template>
  <AppModal :opened="true" size="large" :has-header="false" @modal-close="emits('close')">
    <div class="section t-mb-4">
      <h6 class="is-size-6 t-mb-4">
        {{ t("wizardSelectionPopup.whatToLike") }}
      </h6>
      <div class="t-row is-gutter-narrow">
        <template v-for="offerCategory in offerCategories.data" :key="offerCategory.id">
          <div class="t-col is-6 t-mb-4" v-if="isCxApp() || offerCategory.id !== 'prepare'">
            <CategoryCard
              :is-selected="
                offerCategory.id !== 'govern' &&
                offerCategory.id === offerCategories.selectedData?.[0].id
              "
              :is-disabled="offerCategory.isDisabled"
              :id="offerCategory.id"
              :class="offerCategory.id === 'govern' ? 'is-disabled' : ''"
              @click="
                onSelectionTile({
                  key: 'offerCategories',
                  data: offerCategory,
                  selectionType: 'single'
                })
              "
            >
              <template #title
                >{{ offerCategory.title }}
                <small v-if="offerCategory.id == 'govern'">( coming soon )</small></template
              >
              <template #icon><span v-html="offerCategory.icon" /></template>
            </CategoryCard>
          </div>
        </template>
      </div>
    </div>

    <template
      v-if="
        (offerCategories.selectedData?.[0].id === 'provide' ||
          offerCategories.selectedData?.[0].id === 'prepare') &&
        isCxApp()
      "
    >
      <div class="section t-mb-4">
        <h6 class="is-size-6 t-mb-4">
          {{ t("wizardSelectionPopup.selectSingleUseCase") }}
        </h6>
        <div class="t-row is-gutter-narrow">
          <div class="t-col is-4 t-mb-4" v-for="useCase in useCases.data" :key="useCase._id">
            <UseCaseCard
              :item="useCase"
              :is-selected="
                useCases?.selectedData?.findIndex(
                  (category: any) => category._id === useCase._id
                ) !== -1
                  ? true
                  : false
              "
              @tile-click="
                onSelectionTile({ key: 'useCases', data: useCase, selectionType: 'single' })
              "
            >
              <template #icon
                ><scale-icon name="default-usecase" size="35" fill="#65B8E7"
              /></template>
              <template #title>{{ useCase.name }}</template>
              <template #description>{{ useCase.description }}</template>
            </UseCaseCard>
          </div>
        </div>
      </div>

      <div
        class="section t-mb-4"
        v-if="offerCategories.selectedData?.[0].id === 'provide' && useCases.selectedData?.length"
      >
        <h6 class="is-size-6 t-mb-4">
          {{ t("wizardSelectionPopup.selectDigitalTwin") }}
        </h6>
        <div class="t-row is-gutter-narrow">
          <div
            class="t-col is-4 t-mb-4"
            v-for="digitalTwin in digitalTwinTypes.data"
            :key="digitalTwin._id"
          >
            <UseCaseCard
              :item="digitalTwin"
              :is-selected="
                digitalTwinTypes?.selectedData?.findIndex(
                  (category: any) => category._id === digitalTwin._id
                ) !== -1
                  ? true
                  : false
              "
              @tile-click="
                onSelectionTile({
                  key: 'digitalTwinTypes',
                  data: digitalTwin,
                  selectionType: 'single'
                })
              "
            >
              <template #icon
                ><scale-icon name="default-usecase" size="35" fill="#65B8E7"
              /></template>
              <template #title>{{ digitalTwin.name }}</template>
              <template #description>{{ digitalTwin.description }}</template>
            </UseCaseCard>
          </div>
        </div>
      </div>
    </template>
    <template #actions>
      <div class="t-flex t-flex-justify-content-space-between">
        <div>
          <scale-button @click="emits('close')" variant="secondary" class="t-mr-2">
            {{ t("common.back") }}
          </scale-button>
          <scale-button @click="onClickNext" :disabled="!isNextDisabled">
            {{ t("common.next") }}
          </scale-button>
        </div>
      </div>
    </template>
  </AppModal>
</template>
<script setup lang="ts">
import { useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import AppModal from "@/components/AppModal/AppModal.vue"
import CategoryCard from "./CategoryCard.vue"
import UseCaseCard from "./UseCaseCard.vue"
import { useProviderStore } from "@/store/modules/provider/providerStore"
import { IntProviderState } from "@/store/modules/provider/IntProviderState"
import { ProtectedRoutes } from "@/router/IRoute"
import { computed } from "vue"
import { isCxApp } from "@/common/util/env"

const router = useRouter()
const { t } = useI18n()
const providerStore = useProviderStore()

const emits = defineEmits<{
  (eventName: "close"): void
  (eventName: "onClickAction", payload: string): void
}>()

const { offerCategories, digitalTwinTypes, useCases } = storeToRefs(useProviderStore())

const onSelectionTile = (payload: {
  key: keyof IntProviderState
  data: any
  selectionType: "single" | "multiselect"
}): void => {
  // govern cannot be selected since its page is not available
  if (payload.data.id === "govern") return

  providerStore.setInitiateState({
    key: payload.key,
    data: payload.data,
    selectionType: payload.selectionType
  })
}

const isNextDisabled = computed(() => {
  return offerCategories.value.selectedData?.length
})

const onClickNext = () => {
  const type = offerCategories.value.selectedData?.[0].id
  switch (type) {
    case "prepare":
      // user did NOT select a usecase
      if (!useCases.value.selectedData?.length) {
        router.push({
          name: ProtectedRoutes.prepare.models.pathName
        })
      } else {
        // only if users select a use case then redirect to models overview page
        // and add the query param
        router.push({
          name: ProtectedRoutes.prepare.models.pathName,
          query: { usecase: useCases.value.selectedData?.[0]._id }
        })
      }
      break
    case "provide":
      router.push({
        name: ProtectedRoutes.provider.configure.pathName
      })
      break
    case "consume":
      router.push({
        name: ProtectedRoutes.consume.offers.pathName
      })
      break
  }
  emits("close")
}
</script>
<style lang="scss" scoped>
@import "@/styles/utilities/variables";
@import "@/styles/mixins/typography";

.card--title {
  @include tg-heading-5;
}

.is-disabled {
  opacity: 1;
  color: var(--telekom-color-text-and-icon-disabled);
  cursor: not-allowed;
  pointer-events: none;
}
</style>
